import React, { useEffect, useState } from "react"
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import { get } from "helpers/api_helper"
import { phaseMessage } from "./usePhaseOperation"
// A qualifier by participant key: two Tenis10 siblings under one parent account are two (PRD 16 LR11c).
import { participantKey } from "../../components/participantIdentity"

// Preview refusals that reading again cannot change: the dialog says why, without Retry. A revision conflict keeps Retry, which
// reads the current impact; anything else (a 5xx, no answer, an expired session) stays an impact that could not be verified.
const FINAL_PREVIEW_REFUSALS = ["PHASE_TARGET_NOT_FOUND", "ERROR_PERMISSIONS", "SUPERADMIN_REQUIRED", "INVALID_PHASE_QUOTA", "INVALID_PHASE_RANKING",
  "INVALID_PHASE_DETAILS", "INVALID_PHASE_ACTION"]
const refusalCode = error => {
  const status = error?.response?.status
  return status >= 400 && status < 500 ? error.response.data?.message : undefined
}

export default function PhaseActionDialog({ eventId, competitionId, phaseId, action, details, pending, message, onClose, onConfirm, onRecover }) {
  const [state, setState] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  const [confirmProvisional, setConfirmProvisional] = useState(false)
  const [reason, setReason] = useState("")
  const mlNumber = details?.mlNumber, interGroupRanking = details?.interGroupRanking
  useEffect(() => {
    let active = true
    setState({ loading: true })
    setConfirmProvisional(false)
    get(`/admin/events/${eventId}/competitions/${competitionId}/phases/${phaseId}/preview`, { params: { action, mlNumber, interGroupRanking } })
      .then(response => {
        const data = response?.data
        if (data?.eventId !== eventId || data?.competitionId !== competitionId || data?.phaseId !== phaseId || data?.action !== action) throw new Error("PHASE_CONTEXT_UNAVAILABLE")
        if (active) setState({ data })
      }).catch(error => { if (active) setState({ error: true, code: refusalCode(error) }) })
    return () => { active = false }
  }, [eventId, competitionId, phaseId, action, mlNumber, interGroupRanking, attempt])
  const preview = state.data
  const buttonText = action === "edit" ? (preview?.requiresReset ? "Save changes and reset phase" : "Save changes")
    : ({ generate: "Generate phase", reset: "Reset phase", delete: "Delete phase" }[action])
  return <Modal isOpen toggle={pending ? undefined : onClose} size="lg">
    <ModalHeader toggle={pending ? undefined : onClose}>{action === "edit" ? "Review phase changes" : buttonText}</ModalHeader>
    <ModalBody>
      {state.loading && <p role="status">Checking current phase and matches…</p>}
      {state.error && (FINAL_PREVIEW_REFUSALS.includes(state.code) ? <Alert color="danger">{phaseMessage(state.code)}</Alert>
        : <Alert color="danger">{state.code === "PHASE_REVISION_CONFLICT" ? phaseMessage(state.code) : "The phase impact could not be verified."}{" "}
          <Button color="link" onClick={() => setAttempt(value => value + 1)}>Retry</Button></Alert>)}
      {preview && <>
        {!preview.allowed && <Alert color="warning">{phaseMessage(preview.blocked)}</Alert>}
        {action === "edit" && <dl className="row mb-2">
          <dt className="col-sm-4">Qualified participants</dt><dd className="col-sm-8">{preview.changes.before.mlNumber} → {preview.changes.after.mlNumber}</dd>
          <dt className="col-sm-4">Inter-group ranking</dt><dd className="col-sm-8 text-capitalize">{preview.changes.before.interGroupRanking} → {preview.changes.after.interGroupRanking}</dd>
        </dl>}
        {preview.requiresReset && <Alert color="warning">
          <p className="font-weight-semibold">{action === "delete" ? "The final phase and its matches will be deleted." : "The final phase will be reset."}</p>
          <p>{preview.impact.matches} matches · {preview.impact.results} results · {preview.impact.scheduled} scheduled</p>
          <p className="mb-0">The group matches and results will be preserved.</p>
        </Alert>}
        {action === "generate" && <>
          <p><strong>{preview.available ?? "—"} / {preview.quota ?? "—"} real qualifiers available</strong></p>
          {!!preview.selection?.length && <ul>{preview.selection.map((player, index) => <li key={participantKey(player) ?? index}>
            {player.firstName} {player.lastName}{player.partner && ` / ${player.partner.firstName} ${player.partner.lastName}`} · {player.groupName}{player.groupPosition}
          </li>)}</ul>}
          {preview.provisional && <Alert color="warning">
            <p>Group results are incomplete: {preview.incompleteGroups.map(group => `${group.name} (${group.missingResults} missing)`).join(", ")}.</p>
            <p>Later group results will not automatically change the generated phase.</p>
            <label className="phase-provisional-label mb-0"><input type="checkbox" checked={confirmProvisional} disabled={pending}
              onChange={event => setConfirmProvisional(event.target.checked)} /><span>I confirm this provisional selection.</span></label>
          </Alert>}
        </>}
        {/* Every reset offers a reason; it is required only when the API says recorded results would be discarded (T3 = A). */}
        {(preview.requiresReset || preview.reasonRequired) && <FormGroup>
          <Label for="phase-action-reason">{preview.reasonRequired ? "Reason *" : "Reason (optional)"}</Label>
          <Input id="phase-action-reason" type="textarea" rows={3} maxLength={2000} disabled={pending} value={reason} onChange={event => setReason(event.target.value)} />
          <small className="form-text text-muted">This reason will be saved in Action Logs with the changes and the previous phase state.</small>
        </FormGroup>}
      </>}
      {/* Amber while the result is still open (`pending` covers an unknown one), as in the phase editor; red once it is final. */}
      {message && <Alert color={pending ? "warning" : "danger"}>{message}</Alert>}
    </ModalBody>
    <ModalFooter>
      {pending && onRecover && <Button color="link" onClick={onRecover}>Check result</Button>}
      <Button color="light" onClick={onClose} disabled={pending}>Cancel</Button>
      <Button color={preview?.requiresReset ? "danger" : "primary"} disabled={pending || !preview?.allowed || (preview.reasonRequired && !reason.trim()) || (preview.provisional && !confirmProvisional)}
        onClick={() => onConfirm(preview, { reason: reason.trim(), confirmReset: Boolean(preview.requiresReset), confirmProvisional })}>
        {pending ? "Saving phase…" : buttonText}
      </Button>
    </ModalFooter>
  </Modal>
}
